import * as React from "react";
import { Text } from "@uikit";

import { getCoursePricing, slugify } from "@kalabo/golfsavers-common";

import { isRegionalSite, useRootCourseUrls } from "@utilities/core";

import { Link } from "gatsby";
import { calcCrow } from "@kalabo/golfsavers-common";
import {
  Region,
  Coordinate,
  Course,
  CourseExtended,
} from "@kalabo/golfsavers-common";
import StarRating from "@components/starRating";
import XE from "@components/common/xe";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { useApp } from "@store/app";

interface iCoursesProps {
  courses: Course[];
  region: Region;
  center: Coordinate;
  maxDistance?: number;
}

const OtherCourses = ({
  region,
  courses,
  center,
  maxDistance = 10,
}: iCoursesProps) => {
  const date = useApp(($) => $.date);
  const display_currency = useApp(($) => $.display_currency);

  const golf_courses = courses
    .map((course: Course) => {
      return {
        ...course,
        distance: calcCrow(
          center.latitude,
          center.longitude,
          course.latitude,
          course.longitude
        ),
      };
    })
    .sort((a: CourseExtended, b: CourseExtended) => {
      return a.distance.km - b.distance.km;
    })
    .filter((course: CourseExtended) => {
      return (
        course.latitude !== center.latitude &&
        course.longitude !== center.longitude
      );
    })
    .filter((course: CourseExtended) => {
      return course.distance.km < maxDistance;
    })
    .slice(0, 6);

  if (golf_courses.length === 0) return null;

  return (
    <div className="mb-10 flex-col flex flex-1 overflow-hidden  pt-10">
      <div className="flex justify-between items-center mb-6">
        <Text
          size={21}
          element="h2"
          weight="semibold"
          color="black"
          leading={1.3}
        >
          Other {region.name.en} Golf Courses You May Like
        </Text>
        <Link
          to={
            isRegionalSite()
              ? `/${region.slug}-golf-courses?scroll=true`
              : `/${slugify(region.country.slug)}/${slugify(
                  region.name.en.toLowerCase()
                )}-golf-courses/?scroll=true`
          }
          className="hidden sm:block"
        >
          <Text
            size={15}
            weight="semibold"
            color="black"
            className="flex items-center hover:text-blue underline"
          >
            See all{" "}
            <span>
              <ChevronRightIcon className="w-4" />
            </span>
          </Text>
        </Link>
      </div>
      <div className="grid grid-cols-12  gap-4">
        {golf_courses.map((course: CourseExtended, idx: any) => {
          let hideMobile = idx > 3 ? true : false;

          let rating = course.reviews.average_rating;

          let price_obj = getCoursePricing(null, course.golf_contracts) || 0;

          let featured_image = course.featured_image;
          return (
            <Link
              to={
                isRegionalSite()
                  ? useRootCourseUrls()
                    ? `/${course.slug}`
                    : `/${region.slug}-golf-courses/${course.slug}`
                  : `/${slugify(region.country.slug)}/${slugify(
                      region.name.en.toLowerCase()
                    )}-golf-courses/${course.slug}`
              }
              key={idx}
              className={`group col-span-6 sm:col-span-3 lg:col-span-2 flex-col gap-2  ${
                hideMobile ? "hidden lg:flex" : "flex"
              }`}
            >
              <div className={`rounded-[4px] overflow-hidden bg-black `}>
                <img
                  className="w-full rounded-[4px] transition-all  group-hover:opacity-90"
                  alt={featured_image?.alt || course.name.en}
                  src={`${process.env.GATSBY_IMAGE_PREFIX}${featured_image?.url}?aspect_ratio=3:2&height=115)`}
                />
              </div>
              <div className="flex flex-col gap-1 justify-between flex-1 overflow-hidden">
                <Text
                  size={15}
                  color="black90"
                  weight="semibold"
                  leading={1.3}
                  element="h3"
                  className="line-clamp-2 group-hover:text-blue group-hover:underline "
                >
                  {course.name.en}
                </Text>
                {price_obj.price === 0 ? (
                  <>
                    <Text size={13} color="black90" leading={1.3}>
                      Enquire for price
                    </Text>
                  </>
                ) : (
                  <div className="flex  gap-1 flex-1 items-end">
                    <Text size={11} italic color="black90" leading={1.5}>
                      from
                    </Text>
                    <div className="flex items-end gap-1">
                      <Text size={13} color="black90" leading={1.3}>
                        <XE
                          simplify
                          base_currency={price_obj?.currency}
                          currency={display_currency.code}
                          value={
                            Math.round(Number(price_obj.price) * 100) / 100
                          }
                        />
                      </Text>
                      <Text size={13} color="black90" leading={1.3}>
                        / 18H
                      </Text>
                    </div>
                  </div>
                )}
                <div className="flex gap-1 items-center">
                  <StarRating hideCount rating={rating} size="small" />
                  <Text size={13} color="black60" leading={1.3}>
                    ({course.reviews.count || 0})
                  </Text>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <Link
        to={
          isRegionalSite()
            ? `/${region.slug}-golf-courses`
            : `/${slugify(region.country.slug)}/${slugify(
                region.name.en.toLowerCase()
              )}-golf-courses/`
        }
        className="sm:hidden flex flex-1 justify-center mt-8"
      >
        <Text
          size={15}
          weight="semibold"
          color="black"
          className="flex items-center hover:text-blue underline"
        >
          See all{" "}
          <span>
            <ChevronRightIcon className="w-4" />
          </span>
        </Text>
      </Link>
    </div>
  );
};

export default OtherCourses;
